import type {
  ContractApprovalDecision,
  ContractApprovalSummary,
  ContractResponsibilities,
} from "@closer/shared";
import {
  CONTRACT_APPROVAL_STATUS_NAMES,
  CONTRACT_APPROVAL_TASK_STATUS_NAMES,
  responsibilityTitle,
} from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  Modal,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { StateView } from "../../components/StateView";
import { TaskReassignDialog } from "../../components/TaskReassignDialog";
import { UyeListesiDurumu } from "../../components/UyeListesiDurumu";
import { WorkflowCancelDialog } from "../../components/WorkflowCancelDialog";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { sozlesmeAdaylariSorgusu, sozlesmeSurumSorgusu } from "../../lib/sharedQueries";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";
import { ContractWorkflowStart } from "./ContractWorkflowStart";

/**
 * İÇ ONAY AKIŞI (CLOS-158 `[151]` · CLOS-159 `[152]` · CLOS-160 `[153]`).
 *
 * ### ⚠ Onay, MUTABAKAT DEĞİL
 *
 * Bu bölüm organizasyonun KENDİ karar zinciri; taraflar arası uzlaşma ayrı bölümde.
 * İkisini tek yerde toplasaydık, iç onayı tamamlanan bir sözleşme "taraflar anlaştı"
 * gibi görünürdü.
 *
 * ### ⚠ Aşama sırası ve yeter sayı ekranda görünür
 *
 * Seri akışta sırası gelmemiş aşamanın görevleri `blocked`; ekran bunu olduğu gibi
 * gösteriyor ve karar düğmesi açmıyor. Yeter sayı (quorum) aşamanın kendi alanı: kaç
 * onayla o aşamanın geçtiği kullanıcıdan gizlenmiyor.
 */
export function ContractApprovalSection({
  contractId,
  arsivde = false,
}: {
  contractId: string;
  /**
   * ⚠ Arşivdeki sözleşmede AKIŞ BAŞLATILMIYOR (kusur avı, 26 Ağustos). Sunucu dördünü de
   * reddediyor ("Arşivdeki sözleşme ... "); düğmeyi çizmek, kullanıcıyı formu doldurup
   * 409 yemeye göndermekti. Yürüyen akışın komutları KALIYOR: arşivleme onları bitirmiyor.
   */
  arsivde?: boolean;
}) {
  const { activeTenantId, can, user } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();

  const [baslatAcik, setBaslatAcik] = useState(false);
  const [kararHedef, setKararHedef] = useState<{ approvalId: string; taskId: string } | null>(null);

  const anahtar = tenantKey(tenantId, "contract", contractId, "onaylar");
  const akislar = useQuery({
    queryKey: anahtar,
    queryFn: async () =>
      (await api<ContractApprovalSummary[]>(`/api/contracts/${contractId}/approvals`, { tenantId }))
        .data,
  });

  /**
   * ⚠ BEKLEYEN İŞLER de tazeleniyor (kusur avı, 26 Ağustos). Bu bölümdeki her karar
   * sunucuda bekleyen görev satırını kapatıyor ya da açıyor; yalnız kendi anahtarını
   * tazeleseydik üst bardaki rozet ve Ana Sayfa bölümü eski sayıda kalır, kullanıcı biraz
   * önce bitirdiği işi hâlâ "bekliyor" diye görürdü. Üç tüketici de burada: panel rozeti
   * (CLOS-156 `[149]`), Ana Sayfa bölümü ve tam liste sayfası.
   */
  const yenile = async () => {
    await queryClient.invalidateQueries({ queryKey: anahtar });
    await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "pending-tasks") });
    await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "home") });
    await queryClient.invalidateQueries({ queryKey: ["pending-tasks"] });
  };

  /** Onay bekleyen iptal hedefi; `null` iken diyalog kapalı (CLOS-968). */
  const [iptalHedefi, setIptalHedefi] = useState<string | null>(null);

  /** Devredilecek onay görevi; `null` iken yeniden atama diyaloğu kapalı (CLOS-968). */
  const [devirHedef, setDevirHedef] = useState<{
    approvalId: string;
    taskId: string;
    approverUserId: string;
  } | null>(null);

  /** İptal edilecek TEKİL onay görevi; `null` iken diyalog kapalı (CLOS-1117 · yetim uç B). */
  const [gorevIptalHedefi, setGorevIptalHedefi] = useState<{
    approvalId: string;
    taskId: string;
  } | null>(null);

  /**
   * TEKİL GÖREV İPTALİ (CLOS-1117 · yetim uç B).
   *
   * ⚠ Uç sunucuda vardı (CLOS-924), ekranı yoktu: izne çıkan onaylayıcının görevi yalnız
   * DEVREDİLEBİLİYOR, kapatılamıyordu. Akış iptalinden farkı: diğer onaylayıcılar
   * etkilenmiyor. Quorum matematiği SUNUCUDA: bu görev düşünce aşama hiçbir zaman
   * geçemeyecekse 409 geliyor ve cümlesi olduğu gibi gösteriliyor — ekran o hesabı
   * tekrarlamıyor, iki gerçek olmasın diye.
   */
  /**
   * ⚠ HER NİYET KENDİ ANAHTARINI TAŞIR (BUG-004). Anahtar tıklamaya değil NİYETE bağlı:
   * çift tıklama aynı anahtarı gönderir ve sunucu ikincisini replay sayar. Görev iptali
   * ile imzacı/inceleyici devri AYRI niyetler; tek anahtarı paylaşsalardı biri diğerinin
   * yanıtına sessizce düşerdi.
   */
  const idempotencyIptal = useIdempotencyKey();
  const idempotencyDevir = useIdempotencyKey();

  const gorevIptal = useMutation({
    mutationFn: async (girdi: { approvalId: string; taskId: string; reason?: string }) =>
      api(
        `/api/contracts/${contractId}/approvals/${girdi.approvalId}/tasks/${girdi.taskId}/cancel`,
        {
          method: "POST",
          tenantId,
          /** ⚠ Uç `idempotency()` zinciriyle korunuyor: aynı iptal iki kez yazılmasın. */
          headers: { "idempotency-key": idempotencyIptal.uret() },
          body: JSON.stringify(girdi.reason ? { reason: girdi.reason } : {}),
        },
      ),
    onSuccess: async () => {
      /** ⚠ Başarıdan sonra yeni niyet yeni anahtar alıyor (BUG-004). */
      idempotencyIptal.yenile();
      setGorevIptalHedefi(null);
      await yenile();
      toast.success("Onay görevi iptal edildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Onay görevi iptal edilemedi"),
  });

  const iptal = useMutation({
    mutationFn: async (approvalId: string) =>
      api(`/api/contracts/${contractId}/approvals/${approvalId}/cancel`, {
        method: "POST",
        tenantId,
      }),
    onSuccess: async () => {
      setIptalHedefi(null);
      await yenile();
      toast.success("Onay akışı iptal edildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Onay akışı iptal edilemedi"),
  });

  /**
   * ⚠ ÖN KOŞUL MODAL AÇILMADAN ÖLÇÜLÜYOR — canlı tur bulgusu (4 Eylül).
   *
   * Anahtar, modalın onaylayıcı listesiyle AYNI: iki yerde iki istek değil, tek önbellek.
   */
  const onKosulUyeler = useQuery({
    /** ⚠ İzin de kapıda: yetkisiz görüntüleyicide bu uç HİÇ çağrılmıyor. */
    enabled: !arsivde && can("contract.approve"),
    ...sozlesmeAdaylariSorgusu(tenantId, contractId, "contract.approve"),
  });

  /**
   * ⚠ Yükleme sırasında engel GÖSTERİLMİYOR: liste dönmeden üye sayısı 0 görünür ve ekran
   * bir an "önce kullanıcı davet edin" diye yanlış suçlama yapardı. Modalın içindeki aynı
   * kural son savunma olarak duruyor.
   */
  const onKosul = onKosulUyeler.isSuccess
    ? onayOnKosulEngeli({
        aktifUyeSayisi: (onKosulUyeler.data?.candidates ?? []).length,
      })
    : null;

  /**
   * ⚠ Yetki kapısı BÜTÜN hook'lardan SONRA: erken `return` bir hook'un önüne geçseydi
   * React çağrı sırasını kaybederdi.
   */
  if (!can("contract.approve")) return null;

  const acik = (akislar.data ?? []).find((a) => a.status === "open");

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">İç onay</h2>
        {!acik && !arsivde ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/*
              ⚠ SEBEP DÜĞMENİN YANINDA, MODALIN İÇİNDE DEĞİL — canlı tur bulgusu
              (4 Eylül). Kullanıcı düğmeye basıyor, sürüm seçiyor, onaylayıcı listesini
              açıyor ve ancak orada kendinden başka kimse olmadığını görüyordu. Engel
              modalın içinde düzeltilemediği için modal artık hiç açılmıyor.
            */}
            {onKosul ? (
              <p className="text-xs text-warning" data-testid="onay-on-kosul">
                {onKosul}
              </p>
            ) : null}
            {/*
              ⚠ İKİ YOL YAN YANA (CLOS-1202) ve ikisi de gerekli.

              "Tanımlı akışı başlat" kiracının kendi iş akışı tanımını çalıştırıyor —
              normal yol. "Onay akışı başlat" onaylayıcıları elle seçtiriyor. İkincisini
              kaldırsaydık tanımlı akışı olmayan kiracıların iç onayı tamamen kapanırdı;
              birincisi olmadan ise tanımlı akış hiçbir yerden başlatılamıyordu (uç
              canlıda çalışıyor ama düğmesi yoktu).

              ⚠ ÖN KOŞUL YALNIZ ELLE YOLA AİT ve bu CANLI DOĞRULAMADA bulundu. İlk yazımda
              `disabled={onKosul !== null}` geçiriyordum; ölçüldü ki "kendi başlattığın
              akışın tek onaylayıcısı olamazsın" kuralı `approvals.ts`'te, yani ELLE yolda.
              `workflowInstance.ts` onu uygulamıyor — tanımlı akışın katılımcıları
              tanımdan çözülüyor, kullanıcı seçmiyor.

              Yani düğmeyi ona bağlamak, sunucunun izin verdiği bir işi kullanıcıya
              yasaklamaktı: hiçbir test kırmızı yanmaz, yalnız düğme çizilmez. Tanımlı
              yolun kendi engelleri modalın içinde, çözümleme ucundan geliyor.
            */}
            <ContractWorkflowStart contractId={contractId} />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setBaslatAcik(true)}
              disabled={onKosul !== null}
            >
              Onay akışı başlat
            </Button>
          </div>
        ) : null}
      </div>

      {akislar.isPending ? (
        <Skeleton lines={3} />
      ) : akislar.error ? (
        <StateView
          state={resolveErrorState(akislar.error, {
            idempotent: true,
            overrides: {
              error: { title: "Onay akışı yüklenemedi", description: "Bölüm şu an getirilemedi." },
            },
          })}
          onRetry={() => void akislar.refetch()}
        />
      ) : (akislar.data ?? []).length === 0 ? (
        <p className="text-sm text-fg-muted">Bu sözleşme için henüz onay akışı başlatılmadı.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {(akislar.data ?? []).map((akis) => (
            <div
              key={akis.id}
              className="flex flex-col gap-2 rounded-md border border-border-default p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={akis.status === "open" ? "warning" : "neutral"}>
                  {CONTRACT_APPROVAL_STATUS_NAMES[akis.status]}
                </Badge>
                <span className="text-xs text-fg-soft">
                  Sürüm {akis.versionNumber} · {akis.mode === "serial" ? "Sırayla" : "Aynı anda"} ·{" "}
                  {akis.startedByName}
                  {/*
                    ⚠ SON TARİH KÜNYEDE (rapor 5 §5: her süreçte "Son tarih" zorunlu görünür
                    bilgi). İnceleme ve mutabakat bunu gösteriyordu, onay göstermiyordu —
                    onaycı ne zamana kadar karar vermesi gerektiğini hiçbir yerden okuyamıyordu.
                  */}
                  {akis.dueDate
                    ? ` · Son tarih ${new Date(akis.dueDate).toLocaleDateString("tr-TR")}`
                    : ""}
                </span>
                {akis.status === "open" ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setIptalHedefi(akis.id)}
                  >
                    İptal et
                  </Button>
                ) : null}
              </div>

              {akis.stages.map((asama) => (
                <div key={asama.id} className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-fg-strong">
                    {asama.position + 1}. aşama · {asama.tasks.length} kişiden {asama.quorum} onay
                  </span>
                  <ul className="flex flex-col">
                    {asama.tasks.map((gorev) => (
                      <li
                        key={gorev.id}
                        className="flex flex-wrap items-center justify-between gap-2 border-b border-border-default py-1.5 last:border-0"
                      >
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate text-sm text-fg">{gorev.approverName}</span>
                          {gorev.decisionNote ? (
                            <span className="truncate text-xs text-fg-soft">
                              {gorev.decisionNote}
                            </span>
                          ) : null}
                        </span>
                        <span className="flex items-center gap-2">
                          <Badge tone={gorev.status === "approved" ? "success" : "neutral"}>
                            {CONTRACT_APPROVAL_TASK_STATUS_NAMES[gorev.status]}
                          </Badge>
                          {/* ⚠ Karar yalnız kendi BEKLEYEN görevinde; `blocked` sırası gelmemiş. */}
                          {akis.status === "open" &&
                          gorev.status === "pending" &&
                          gorev.approverUserId === user?.id ? (
                            <Button
                              type="button"
                              size="sm"
                              onClick={() =>
                                setKararHedef({ approvalId: akis.id, taskId: gorev.id })
                              }
                            >
                              Karar ver
                            </Button>
                          ) : null}
                          {/*
                           * ⚠ Devretme BAŞKASININ görevinde de görünür (CLOS-968): izne
                           * çıkan onaylayıcının işini akışı yürüten kişi devreder. `blocked`
                           * görev devredilmiyor — sırası gelmemiş bir görevin sorumlusunu
                           * değiştirmek, sunucunun "zaten sonuçlanmış" kapısına takılmadan
                           * aşama kurgusunu ekrandan sessizce değiştirmek olurdu.
                           */}
                          {akis.status === "open" && gorev.status === "pending" ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setDevirHedef({
                                  approvalId: akis.id,
                                  taskId: gorev.id,
                                  approverUserId: gorev.approverUserId,
                                })
                              }
                            >
                              Devret
                            </Button>
                          ) : null}
                          {/*
                           * ⚠ Görev iptali "Devret" ile AYNI kapıdan (CLOS-1117 · yetim uç B):
                           * açık akış + bekleyen görev. Akış seviyesindeki "İptal et" bütün
                           * akışı kapatıyor; bu yalnız BU satırın görevini.
                           */}
                          {akis.status === "open" && gorev.status === "pending" ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              data-testid="onay-gorev-iptal-ac"
                              onClick={() =>
                                setGorevIptalHedefi({ approvalId: akis.id, taskId: gorev.id })
                              }
                            >
                              İptal et
                            </Button>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <OnayBaslatDialog
        open={baslatAcik}
        contractId={contractId}
        onClose={() => setBaslatAcik(false)}
        onDone={async () => {
          setBaslatAcik(false);
          await yenile();
        }}
      />
      <OnayKararDialog
        open={kararHedef !== null}
        contractId={contractId}
        hedef={kararHedef}
        onClose={() => setKararHedef(null)}
        onDone={async () => {
          setKararHedef(null);
          await yenile();
        }}
      />

      <WorkflowCancelDialog
        open={iptalHedefi !== null}
        onClose={() => setIptalHedefi(null)}
        title="Onay akışını iptal et"
        description="Açık onay görevleri kapanır ve sözleşme onay beklemez hale gelir. Onay için akışı baştan başlatmanız gerekir."
        pending={iptal.isPending}
        onConfirm={() => iptalHedefi && iptal.mutate(iptalHedefi)}
      />

      <OnayGoreviIptalDialog
        open={gorevIptalHedefi !== null}
        pending={gorevIptal.isPending}
        onClose={() => setGorevIptalHedefi(null)}
        onConfirm={(reason) =>
          gorevIptalHedefi &&
          gorevIptal.mutate({ ...gorevIptalHedefi, ...(reason ? { reason } : {}) })
        }
      />

      {/*
       * ⚠ Gerekçe ONAYDA ZORUNLU (`config/workflowCancelReassign.json`: approval →
       * `requireReasonForReassign: true`): devir, kararı kimin verdiğini değiştiriyor ve
       * bu denetim izinde sebepsiz kalmamalı. Sunucu da aynı kuralı uyguluyor.
       */}
      <TaskReassignDialog
        open={devirHedef !== null}
        onClose={() => setDevirHedef(null)}
        tenantId={tenantId}
        contractId={contractId}
        title="Onay görevini devret"
        assigneeLabel="Yeni onaylayıcı"
        reasonRequired
        currentAssigneeUserId={devirHedef?.approverUserId}
        successMessage="Onay görevi devredildi"
        fallbackErrorMessage="Görev devredilemedi"
        onDone={async () => {
          /** ⚠ Devir başarılıysa yeni niyet yeni anahtar alıyor (BUG-004). */
          idempotencyDevir.yenile();
          await yenile();
        }}
        reassign={async ({ userId, reason }) =>
          api(
            `/api/contracts/${contractId}/approvals/${devirHedef!.approvalId}/tasks/${devirHedef!.taskId}/reassign`,
            {
              method: "POST",
              tenantId,
              headers: { "idempotency-key": idempotencyDevir.uret() },
              body: JSON.stringify({ newApproverUserId: userId, ...(reason ? { reason } : {}) }),
            },
          )
        }
      />
    </Card>
  );
}

/** Sunucudaki `reasonMinLength` (config/workflowCancelReassign.json). */
const IPTAL_GEREKCE_ASGARI = 10;

/**
 * TEKİL ONAY GÖREVİ İPTAL DİYALOĞU (CLOS-1117 · yetim uç B).
 *
 * ### ⚠ Neden ortak `WorkflowCancelDialog` DEĞİL
 *
 * O bileşen AKIŞ seviyesindeki gövdesiz `.../cancel` uçları için yazıldı ve gerekçe alanı
 * BİLEREK yok. Görev seviyesindeki uç gövde alıyor (`cancelContractApprovalTaskSchema`);
 * gerekçe onayda İSTEĞE BAĞLI (`requireReasonForCancel: false`) ama girildiyse sunucu
 * `reasonMinLength`'i (10) uyguluyor. Ekran o sınırı kullanıcıyı boşuna reddettirmemek
 * için önden gösteriyor; asıl kapı `applyReasonPolicy`.
 *
 * ⚠ Gerekçe boşsa gövdeye `reason` HİÇ girmiyor: `strictObject` boş metni de doğrular ama
 * "gerekçe yok" ile "gerekçe boş metin" audit'te iki farklı şey olurdu.
 */
function OnayGoreviIptalDialog({
  open,
  pending,
  onClose,
  onConfirm,
}: {
  open: boolean;
  pending: boolean;
  onClose: () => void;
  onConfirm: (reason: string | undefined) => void;
}) {
  const [gerekce, setGerekce] = useState("");

  // Her açılış temiz başlar: önceki denemenin gerekçesi başka bir göreve taşınmaz.
  useEffect(() => {
    if (open) setGerekce("");
  }, [open]);

  const kirpilmis = gerekce.trim();
  const eksik = kirpilmis.length > 0 && kirpilmis.length < IPTAL_GEREKCE_ASGARI;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Onay görevini iptal et"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            type="button"
            variant="danger"
            loading={pending}
            disabled={eksik || pending}
            data-testid="onay-gorev-iptal-onayla"
            onClick={() => onConfirm(kirpilmis || undefined)}
          >
            İptal et
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-fg-muted">
          Yalnız bu onaylayıcının görevi kapanır; akış ve diğer onaylayıcılar etkilenmez. Aşamanın
          yeter sayısı artık sağlanamayacaksa sunucu iptali reddeder.
        </p>
        <FormField
          label="Gerekçe (isteğe bağlı)"
          error={eksik ? `Gerekçe en az ${IPTAL_GEREKCE_ASGARI} karakter olmalıdır` : undefined}
        >
          <Textarea
            id="onay-gorev-iptal-gerekce"
            value={gerekce}
            onChange={(olay) => setGerekce(olay.target.value)}
            rows={3}
            placeholder="Görevin neden iptal edildiğini yazın."
          />
        </FormField>
      </div>
    </Modal>
  );
}

/**
 * Onay akışı başlatma.
 *
 * ⚠ Tek aşama sunuluyor ve bu bilinçli: çok aşamalı zincir ancak gerçek bir ihtiyaç
 * doğduğunda ekranı hak eder. Sunucu çok aşamayı destekliyor; ekran bugün en yaygın
 * kurulumu kolaylaştırıyor, kuralı değiştirmiyor.
 */
/**
 * ONAY AKIŞI BAŞLATMANIN ÖNÜNDEKİ ENGEL — saf ve test edilebilir.
 *
 * ⚠ Diyalogun içinde bir ifade olarak duruyordu; buraya çıkarıldı çünkü ölçülmesi
 * gereken şey bir React ağacı değil bir KARAR: hangi durumda hangi cümle söyleniyor.
 *
 * ⚠ SIRA KARARIN PARÇASI: eksik seçim engeli, "başka kişi gerekir" engelinden ÖNCE.
 * Hiç onaylayıcı seçmemiş kullanıcıya "en az bir başka kişi gerekir" demek, onu yanlış
 * sorunu çözmeye yönlendirirdi.
 */
export function onayBaslatmaEngeli(girdi: {
  versionId: string;
  secilenler: string[];
  kullaniciId: string | undefined;
  /**
   * Çalışma alanındaki aktif üye sayısı — BİLİNMİYORSA verilmez.
   *
   * ⚠ Opsiyonel oluşu kasıtlı: sayı yüklenmeden `0` varsaymak, her ekranda bir an
   * "önce kullanıcı davet edin" diye yanlış suçlama yapardı.
   */
  aktifUyeSayisi?: number;
}): string | null {
  /**
   * ⚠ ÖN KOŞUL İLK SIRADA — canlı turda ölçüldü (4 Eylül). Düzeltilebilir eksikler
   * (sürüm, onaylayıcı seçimi), çaresi olmayan engelin önüne geçmemeli.
   */
  if (girdi.aktifUyeSayisi !== undefined) {
    const onKosul = onayOnKosulEngeli({ aktifUyeSayisi: girdi.aktifUyeSayisi });
    if (onKosul !== null) return onKosul;
  }
  if (girdi.versionId === "") return "Önce onaylanacak sürümü seçin.";
  if (girdi.secilenler.length === 0) return "En az bir onaylayıcı seçin.";
  if (girdi.secilenler.length === 1 && girdi.secilenler[0] === girdi.kullaniciId) {
    return "Kendi başlattığınız akışın tek onaylayıcısı olamazsınız; listeden en az bir başka kişi seçin.";
  }
  return null;
}

/**
 * İÇ ONAYIN ÖN KOŞULU — modalın İÇİNDE düzeltilemeyen engel.
 *
 * ⚠ "Kendi başlattığınız akışın tek onaylayıcısı olamazsınız" kuralı doğru, ama çalışma
 * alanında başka kimse yoksa o kuralın modal içinde bir çaresi YOK: kullanıcının başka
 * bir ekrana gidip birini davet etmesi gerekiyor. Tek kişilik yeni bir çalışma alanında
 * bu kaçınılmaz hâl ve kullanıcı bunu ancak onaylayıcı listesini açıp kendinden başka
 * kimse olmadığını görerek anlıyordu — yani formu doldurduktan SONRA.
 */
export function onayOnKosulEngeli(girdi: { aktifUyeSayisi: number }): string | null {
  if (girdi.aktifUyeSayisi < 2) {
    return (
      "İç onay için sizden başka en az bir kullanıcı gerekiyor; kendi başlattığınız akışın " +
      "tek onaylayıcısı olamazsınız. Önce çalışma alanınıza kullanıcı davet edin."
    );
  }
  return null;
}

function OnayBaslatDialog({
  open,
  contractId,
  onClose,
  onDone,
}: {
  open: boolean;
  contractId: string;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const { activeTenantId, user } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();

  const [versionId, setVersionId] = useState("");
  const [mode, setMode] = useState<"serial" | "parallel">("parallel");
  const [secilenler, setSecilenler] = useState<string[]>([]);
  const [quorum, setQuorum] = useState("1");
  /**
   * SON TARİH (CLOS-1111 · rapor 5 §7.2).
   *
   * ⚠ Bu alan onay modalında HİÇ YOKTU. İnceleme ve mutabakat modalleri son tarih
   * soruyordu, onay sormuyordu — yani onaycıya ne zamana kadar karar vermesi
   * gerektiği hiçbir yerde söylenmiyordu. Sunucu alanı zaten destekliyordu
   * (`contract_approvals.due_date`); eksik olan yalnız ekrandı.
   */
  const [dueDate, setDueDate] = useState("");

  const versiyonlar = useQuery({
    enabled: open,
    ...sozlesmeSurumSorgusu(tenantId, contractId),
  });

  /**
   * ⚠ ADAYLAR YETENEĞE GÖRE SÜZÜLÜ (Mega E2E BUG-009/009b): seçici `contract.approve`
   * taşımayan üyeleri listeliyordu ve seçilince sunucu 403 dönüyordu. Süzgeç sunucuda,
   * kapının kendi kuralıyla — istemcide ikinci bir kopya yazmıyoruz.
   */
  const uyeler = useQuery({
    enabled: open,
    ...sozlesmeAdaylariSorgusu(tenantId, contractId, "contract.approve"),
  });

  /**
   * ÖNERİ KAYNAĞI (CLOS-1126 · UAT-48): sayfadaki sorumluluk listesiyle AYNI anahtar.
   * Yalnız ön işaretleme; sunucu şeması ve karar yetkisi değişmez — öneri authority üretmez.
   */
  const sorumlular = useQuery({
    enabled: open,
    queryKey: tenantKey(tenantId, "contract", contractId, "sorumlular"),
    queryFn: async () =>
      (
        await api<ContractResponsibilities>(`/api/contracts/${contractId}/responsibilities`, {
          tenantId,
        })
      ).data,
    retry: false,
  });

  useEffect(() => {
    if (!open) return;
    setSecilenler([]);
    setQuorum("1");
    setMode("parallel");
    oneriUygulandi.current = false;
  }, [open]);
  useEffect(() => {
    if (open) setVersionId(versiyonlar.data?.[0]?.id ?? "");
  }, [open, versiyonlar.data]);
  /**
   * PREFILL (CLOS-1126 · UAT-48): Sözleşme Sorumlusu onaylayıcı adayı olarak önceden işaretli;
   * kendisiyse önerilmez (tek onaylayıcı kendisi olamaz kuralı). Yalnız öneri; yetki vermez.
   */
  const oneriUygulandi = useRef(false);
  const oneri = (() => {
    const satir = sorumlular.data?.owner;
    if (!satir?.userId || satir.userId === user?.id) return null;
    const uye = uyeler.data?.candidates.find((u) => u.userId === satir.userId);
    /** ⚠ Aday listesi zaten yalnız aktif üyelikleri taşıyor (BUG-002). */
    if (!uye) return null;
    return { userId: satir.userId, ad: uye.name, unvan: responsibilityTitle(satir) };
  })();
  const oneriUserId = oneri?.userId ?? null;
  useEffect(() => {
    if (!open || !oneriUserId || oneriUygulandi.current) return;
    oneriUygulandi.current = true;
    setSecilenler((onceki) => (onceki.length === 0 ? [oneriUserId] : onceki));
  }, [open, oneriUserId]);

  const baslat = useMutation({
    mutationFn: async () =>
      api(`/api/contracts/${contractId}/approvals`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({
          versionId,
          mode,
          /**
           * ⚠ SIRALI AKIŞ GERÇEKTEN SIRALI (Mega E2E BUG-010).
           *
           * Burası mod ne olursa olsun TEK aşama gönderiyordu. Canlı ölçümde "Sırayla
           * (seri)" seçilen akış `mode='serial'` yazıyor ama tek `contract_approval_stages`
           * satırıyla doğuyordu: iki onaylayıcı da AYNI ANDA karar verebiliyordu. Kayıt
           * "sıralı" diyor, davranış paralel — denetimde okunan şey yanlış oluyordu.
           *
           * Sunucu sıralılığı zaten doğru uyguluyor (sonraki aşama ancak öncekisi bitince
           * `blocked` → `pending` oluyor); eksik olan, istemcinin aşamaları KURMASIYDI.
           *
           * ⚠ Seri akışta her aşama TEK onaylayıcı, yeter sayı 1: bir aşamanın içinde sıra
           * kavramı yoktur. Sıra, işaretleme sırasından geliyor.
           * ⚠ Yeter sayı alanı seri modda ekranda da gizli — sorulmayan bir şey gövdeye
           * uydurulmuyor.
           */
          stages:
            mode === "serial"
              ? secilenler.map((userId) => ({ approverUserIds: [userId], quorum: 1 }))
              : [{ approverUserIds: secilenler, quorum: Number(quorum) }],
          /**
           * ⚠ Gün SONU: "5 Eylül'e kadar" denildiğinde 5 Eylül akşamı hâlâ zamanında.
           * ⚠ Son tarih ZORUNLU (ADR-0065); düğme boş tarihte kapalı olduğu için HER ZAMAN gider.
           */
          dueDate: new Date(`${dueDate}T23:59:59`).toISOString(),
        }),
      }),
    onSuccess: async () => {
      toast.success("Onay akışı başlatıldı");
      await onDone();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Onay akışı başlatılamadı"),
  });

  const aktifUyeler = uyeler.data?.candidates ?? [];

  /**
   * ⚠ ENGELİN SEBEBİ O ANKİ DURUMA GÖRE SÖYLENİYOR — canlı turda ölçüldü (4 Eylül).
   *
   * Düğme doğru biçimde pasifti ve kural da ekranda yazıyordu; ama kural HER ZAMAN
   * görünen bir dipnottu ve "şu an bunu neden yapamıyorsun" sorusunu cevaplamıyordu.
   * Tek kişilik yeni bir çalışma alanında sonuç şu oluyordu: kullanıcı kendini seçiyor,
   * düğmeye basıyor, hiçbir şey olmuyor ve nedenini bulamıyor. Koyu temada `opacity-50`
   * tek başına yeterli bir işaret değil.
   *
   * ⚠ Sıra ÖNEMLİ: eksik seçim engeli, "başka kişi gerekir" engelinden ÖNCE. Hiç
   * onaylayıcı seçmemiş kullanıcıya "en az bir başka kişi gerekir" demek, yanlış sorunu
   * çözmeye yönlendirirdi.
   */
  /**
   * ⚠ Üye sayısı SON SAVUNMA olarak burada da geçiliyor: bölümdeki düğme ön koşulu zaten
   * kapatıyor, ama modal bir gün başka bir yerden açılırsa kural yine uygulanır. Sayı
   * yüklenmemişken (`isSuccess` değilse) geçilmiyor — yanlış suçlama yapmasın.
   */
  const engel = onayBaslatmaEngeli({
    versionId,
    secilenler,
    kullaniciId: user?.id,
    ...(uyeler.isSuccess ? { aktifUyeSayisi: aktifUyeler.length } : {}),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Onay akışı başlat"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={() => baslat.mutate()}
            loading={baslat.isPending}
            /**
             * ⚠ Kendi başlattığı akışın TEK onaylayıcısı olamaz (sunucunun kuralı, kusur
             * avı 26 Ağustos). Ekran bunu dipnotta yazıyordu ama düğme açık kalıyordu:
             * kullanıcı kendini seçip Başlat'a basıyor ve 409 alıyordu. Kural artık
             * yazıldığı yerde uygulanıyor.
             */
            disabled={engel !== null || dueDate === ""}
          >
            Başlat
          </Button>
        </>
      }
    >
      {versiyonlar.isPending || uyeler.isPending ? (
        <div role="status" aria-label="Seçenekler yükleniyor">
          <Skeleton lines={3} />
        </div>
      ) : (versiyonlar.data ?? []).length === 0 ? (
        <p className="text-sm text-fg-muted">
          Onay için önce bir sürüm oluşturmalısınız; onay değişmez bir metne verilir.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <FormField label="Onaylanacak sürüm">
            <Select
              id="onay-versiyon"
              value={versionId}
              onChange={(olay) => setVersionId(olay.target.value)}
            >
              {(versiyonlar.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  Sürüm {v.versionNumber} · {new Date(v.createdAt).toLocaleDateString("tr-TR")}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="Onay sırası">
            <Select
              id="onay-mod"
              value={mode}
              onChange={(olay) => setMode(olay.target.value as "serial" | "parallel")}
            >
              <option value="parallel">Aynı anda (paralel)</option>
              <option value="serial">Sırayla (seri)</option>
            </Select>
          </FormField>

          <fieldset className="flex flex-col gap-1">
            <legend className="text-sm font-medium text-fg-strong">Onaylayıcılar</legend>
            {aktifUyeler.map((u) => (
              <Checkbox
                key={u.userId}
                label={u.name}
                checked={secilenler.includes(u.userId)}
                onChange={(olay) =>
                  setSecilenler((onceki) =>
                    olay.target.checked
                      ? [...onceki, u.userId]
                      : onceki.filter((x) => x !== u.userId),
                  )
                }
              />
            ))}
            <UyeListesiDurumu hata={uyeler.error} bosMu={aktifUyeler.length === 0} />
            {oneri ? (
              <p className="text-xs text-fg-soft" data-testid="onaylayici-onerisi">
                Öneri: {oneri.unvan} {oneri.ad} önceden işaretlendi; öneri karar yetkisi vermez.
              </p>
            ) : null}
          </fieldset>

          {/*
            ⚠ YETER SAYI YALNIZ EŞZAMANLI AKIŞTA (BUG-010): seri akışta her aşama tek
            onaylayıcı taşıyor ve yeter sayı tanımı gereği 1. Alanı açık bırakmak,
            gövdeye hiç gitmeyen bir sayı sormak olurdu.
          */}
          {mode === "parallel" ? (
            <FormField label="Yeter sayı (kaç onay gerekli)">
              <Input
                id="onay-quorum"
                type="number"
                min={1}
                max={Math.max(secilenler.length, 1)}
                value={quorum}
                onChange={(olay) => setQuorum(olay.target.value)}
              />
            </FormField>
          ) : null}

          <FormField
            label="Son tarih"
            required
            hint="Süreç başlatılırken son tarih seçilmelidir (ADR-0065)."
          >
            <Input
              id="onay-son-tarih"
              type="date"
              required
              value={dueDate}
              onChange={(olay) => setDueDate(olay.target.value)}
            />
          </FormField>
          {/*
            ⚠ Sabit dipnot yerine O ANKİ engel. Engel yokken hiçbir şey yazmıyor: her
            zaman görünen bir uyarı, gerçekten engellendiği anda da fark edilmez.
          */}
          {engel ? (
            <p className="text-xs text-warning" data-testid="onay-engel">
              {engel}
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

/** Onay kararı — üç seçenek; ret ve değişiklik talebi gerekçe istiyor. */
function OnayKararDialog({
  open,
  contractId,
  hedef,
  onClose,
  onDone,
}: {
  open: boolean;
  contractId: string;
  hedef: { approvalId: string; taskId: string } | null;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();

  const [decision, setDecision] = useState<ContractApprovalDecision>("approved");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setDecision("approved");
    setNote("");
  }, [open]);

  const karar = useMutation({
    mutationFn: async () => {
      if (!hedef) throw new Error("Görev seçilmedi");
      return api(
        `/api/contracts/${contractId}/approvals/${hedef.approvalId}/tasks/${hedef.taskId}/decide`,
        {
          method: "POST",
          tenantId,
          body: JSON.stringify({ decision, ...(note.trim() ? { note: note.trim() } : {}) }),
        },
      );
    },
    onSuccess: async () => {
      toast.success("Onay kararı kaydedildi");
      await onDone();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Karar kaydedilemedi"),
  });

  const gerekceZorunlu = decision !== "approved";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Onay kararı"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={() => karar.mutate()}
            loading={karar.isPending}
            disabled={gerekceZorunlu && note.trim() === ""}
          >
            Kararı kaydet
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-xs text-fg-soft">
          Karar geri alınamaz. Ret, akışı hemen kapatır; değişiklik talebi metnin yeniden
          çalışılmasını ister.
        </p>
        <FormField label="Karar">
          <Select
            id="onay-karar"
            value={decision}
            onChange={(olay) => setDecision(olay.target.value as ContractApprovalDecision)}
          >
            <option value="approved">Onaylıyorum</option>
            <option value="rejected">Reddediyorum</option>
            <option value="changes_requested">Değişiklik istiyorum</option>
          </Select>
        </FormField>
        <FormField label={gerekceZorunlu ? "Gerekçe (zorunlu)" : "Not (opsiyonel)"}>
          <Input
            id="onay-not"
            value={note}
            onChange={(olay) => setNote(olay.target.value)}
            placeholder="Örn. Bedel maddesi bütçeyi aşıyor"
          />
        </FormField>
      </div>
    </Modal>
  );
}
